import React from 'react';
import { Avatar } from '@components/atoms/Avatar';
import { UserInsideArea } from '@models/usersInsideArea.model';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';

interface UserInsideRowProps {
  user: UserInsideArea;
}

const formatTime = (entry: string | Date): string => {
  const d = typeof entry === 'string' ? new Date(entry) : entry;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (entry: string | Date): string => {
  const d = typeof entry === 'string' ? new Date(entry) : entry;
  const today = new Date();
  const isSameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (isSameDay) return 'Today';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatDuration = (minutes: number): string => {
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (hours < 24) return remaining ? `${hours}h ${remaining}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours ? `${days}d ${remHours}h` : `${days}d`;
};

export const UserInsideRow: React.FC<UserInsideRowProps> = ({ user }) => {
  const isInside = user.isInside;
  const eventTime = isInside ? user.entryTime : user.exitTime ?? user.entryTime;
  const eventIcon = isInside ? (
    <LoginIcon sx={{ fontSize: 14 }} />
  ) : (
    <LogoutIcon sx={{ fontSize: 14 }} />
  );
  const eventLabel = isInside ? 'Entered' : 'Exited';

  return (
    <div className="bg-white rounded-lg ring-1 ring-gray-900/5 shadow-sm p-4 flex items-start gap-3 transition-shadow hover:shadow-md">
      <div className="flex-shrink-0">
        <Avatar
          src={user.avatar || undefined}
          text={user.fullName}
          alt={user.fullName}
          size="lg"
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {user.fullName}
            </p>
            {user.email && (
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            )}
          </div>
          {user.role && (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-50 text-indigo-700 uppercase tracking-wide flex-shrink-0">
              {user.role}
            </span>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-2 text-xs">
          <div className="inline-flex items-center gap-1.5 text-gray-500">
            {eventIcon}
            <span>
              {eventLabel} {formatDate(eventTime)} at{' '}
              <span className="font-medium text-gray-700">
                {formatTime(eventTime)}
              </span>
            </span>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${
              isInside
                ? 'bg-green-50 text-green-700'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            <AccessTimeIcon sx={{ fontSize: 12 }} />
            {isInside
              ? `${formatDuration(user.durationMinutes)} inside`
              : `Stayed ${formatDuration(user.durationMinutes)}`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default UserInsideRow;
